import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import config from "../config";
import { HttpErrorMessageService } from "../services/httpError/HttpErrorMessageService";
import { POST_FAKE_LOGIN, POST_REFRESH_TOKEN } from "./url_helper";

const { api } = config;

// default
axios.defaults.baseURL = api.API_URL;
// content type - sẽ được set trong interceptor
// axios.defaults.headers.post["Content-Type"] = "application/json";

// Đọc token từ localStorage khi module load
const authUser: any = localStorage.getItem("authUser")
const initToken = authUser ? (JSON.parse(authUser).token || JSON.parse(authUser).access_token) : null;
if (initToken)
  axios.defaults.headers.common["Authorization"] = "Bearer " + initToken;

// Request interceptor: Gắn token mới nhất từ localStorage trước mỗi request
axios.interceptors.request.use(
  (config) => {
    const stored = localStorage.getItem("authUser");
    if (stored) {
      const user = JSON.parse(stored);
      const latestToken = user.token || user.access_token;
      if (latestToken) {
        config.headers["Authorization"] = "Bearer " + latestToken;
      }
    }
    
    // Set Content-Type cho request không phải FormData
    if (config.data instanceof FormData) {
      // Để axios tự động set Content-Type với boundary cho FormData
      // Không set Content-Type manually
    } else {
      // Set Content-Type mặc định cho JSON
      if (!config.headers["Content-Type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Đăng ký lắng nghe sự kiện "gặp 403 Forbidden" từ bất kỳ API call nào trong app — dùng để
// AuthProtected tự fetch lại quyền mới nhất (xem mục 9, README_SERCURITY.md: quyền vừa bị đổi ở BE
// nên FE nhận 403 dù state.Permission trong Redux vẫn đang cho phép). Không dispatch Redux action
// trực tiếp ở đây để tránh circular import (api_helper.ts <-> slices/permission/thunk.ts <->
// helpers/fakebackend_helper.ts <-> api_helper.ts) — nơi gọi (AuthProtected) tự dispatch.
type ForbiddenListener = (requestUrl?: string) => void;
const forbiddenListeners: ForbiddenListener[] = [];

const notifyForbidden = (requestUrl?: string) => {
  forbiddenListeners.forEach((listener) => listener(requestUrl));
};

export const onForbidden = (listener: ForbiddenListener): (() => void) => {
  forbiddenListeners.push(listener);
  return () => {
    const idx = forbiddenListeners.indexOf(listener);
    if (idx !== -1) forbiddenListeners.splice(idx, 1);
  };
};

// Đăng ký lắng nghe sự kiện "gặp 401 Unauthorized" từ BẤT KỲ API call nào trong app (trừ chính
// request Login/RefreshToken — 401 ở 2 request đó là kết quả nghiệp vụ bình thường (sai mật khẩu /
// refresh token đã hết hạn), đã có xử lý riêng ở AuthProtected.tsx và Login.tsx, không phải "phiên
// đang dùng bị hết hạn giữa chừng"). Cùng pattern với onForbidden/notifyForbidden ở trên — không
// dispatch Redux/toast trực tiếp ở đây để tránh circular import, nơi lắng nghe (AuthProtected) tự lo.
const EXCLUDED_401_URLS = [POST_FAKE_LOGIN, POST_REFRESH_TOKEN];
type SessionExpiredListener = (requestUrl?: string) => void;
const sessionExpiredListeners: SessionExpiredListener[] = [];

const notifySessionExpired = (requestUrl?: string) => {
  sessionExpiredListeners.forEach((listener) => listener(requestUrl));
};

export const onSessionExpired = (listener: SessionExpiredListener): (() => void) => {
  sessionExpiredListeners.push(listener);
  return () => {
    const idx = sessionExpiredListeners.indexOf(listener);
    if (idx !== -1) sessionExpiredListeners.splice(idx, 1);
  };
};

// Tự thử lại request ĐỌC (GET) khi lỗi tạm thời: không có response (axios "Network Error" — proxy/
// Cloudflare cắt kết nối, mất mạng chớp nhoáng) hoặc gateway báo service bận/quá hạn (502/503/504,
// Cloudflare 520-524). Các job nặng (sinh vé, xuất gói in/ảnh QR) chạy cùng process API nên thỉnh thoảng
// có request bị nghẽn — thử lại sau vài trăm ms thường thành công. KHÔNG retry POST/PUT/DELETE để tránh
// tạo trùng dữ liệu, không retry request đã bị huỷ chủ động.
const RETRYABLE_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const MAX_GET_RETRIES = 2;

const shouldRetryRequest = (error: any): boolean => {
  const requestConfig = error?.config;
  if (!requestConfig || axios.isCancel(error)) return false;
  if ((requestConfig.method || 'get').toLowerCase() !== 'get') return false;
  if ((requestConfig.__retryCount || 0) >= MAX_GET_RETRIES) return false;
  const status = error.response?.status;
  return !error.response || RETRYABLE_STATUSES.has(status);
};

// Response interceptor: Handle errors và đảm bảo luôn trả về serializable error
axios.interceptors.response.use(
   response => {
      return response.data;
    },
    async error => {
      if (shouldRetryRequest(error)) {
        const requestConfig = error.config;
        requestConfig.__retryCount = (requestConfig.__retryCount || 0) + 1;
        // Backoff 0.8s, 1.6s — đủ để thread pool / gateway hồi lại.
        await new Promise((resolve) => setTimeout(resolve, 800 * 2 ** (requestConfig.__retryCount - 1)));
        return axios(requestConfig);
      }

      // Tạo serializable error object
      const serializedError: any = {
        message: error.message || 'An error occurred',
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      };

      // Nếu có response data với message, ưu tiên sử dụng
      if (error.response?.data?.message) {
        serializedError.message = error.response.data.message;
      } else if (error.response?.data?.errors && typeof error.response.data.errors === 'object') {
        // Mục 10, README.md — lỗi 400 do model validation tự động của [ApiController] (Data
        // Annotations) trả về ValidationProblemDetails, KHÔNG có field "message" mà có "errors":
        // { TenTruong: ["Loi 1", "Loi 2"] }. Nếu không xử lý riêng, serializedError.message rơi về
        // message kỹ thuật mặc định của axios ("Request failed with status code 400") — gộp toàn bộ
        // message field-level (thường là tiếng Việt do BE viết sẵn) thành 1 chuỗi để hiển thị đúng.
        const fieldMessages = Object.values(error.response.data.errors as Record<string, string[]>)
          .flat()
          .filter((msg): msg is string => typeof msg === 'string' && msg.length > 0);
        if (fieldMessages.length > 0) {
          serializedError.message = fieldMessages.join(' ');
        } else if (error.response?.data?.detail) {
          serializedError.message = error.response.data.detail;
        }
      } else if (error.response?.data?.detail) {
        serializedError.message = error.response.data.detail;
      }

      // Ghi đè bằng message thân thiện tiếng Việt cho các status đã định nghĩa (403...) — áp dụng
      // SAU bước ưu tiên message từ BE ở trên, vì message kỹ thuật/tiếng Anh dù từ BE hay mặc định
      // của axios cũng không được phép hiển thị thẳng ra UI (mục 3, README_SERCURITY.md). Đây là
      // service check chung, áp dụng 1 lần tại đây cho MỌI API call trong app.
      serializedError.message = HttpErrorMessageService.resolve(
        serializedError.status,
        serializedError.message,
      );

      if (error.response?.status === 403) {
        notifyForbidden(error.config?.url);
      }

      if (
        error.response?.status === 401 &&
        !EXCLUDED_401_URLS.some((url) => error.config?.url?.includes(url))
      ) {
        notifySessionExpired(error.config?.url);
      }

      return Promise.reject(serializedError);
    },
);
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token : string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class APIClient {
  /**
   * Fetches data from the given URL
   */
  get = (url: string, params?: any): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        // README_RULE.md mục 1 — bỏ qua param rỗng/null/undefined thay vì nối thẳng vào query
        // string: nếu không lọc ở đây, mọi nơi gọi `api.get(url, { Foo: undefined })` quên tự
        // cleanParams() sẽ bị coerce thành chuỗi literal "undefined"/"null", khiến backend
        // (ASP.NET Core) không parse được và trả 400 — đã xảy ra nhiều lần vì dễ quên bọc thủ công
        // ở từng nơi gọi, nên lọc ngay tại đây để áp dụng cho TOÀN BỘ lời gọi api.get().
        if (value === null || value === undefined || value === '') {
          return;
        }
        // Giá trị mảng (VD Statuses=[2,4,5]) phải sinh nhiều cặp key=value LẶP LẠI
        // (Statuses=2&Statuses=4&Statuses=5) — trước đây nối bằng dấu phẩy (Statuses=2,4,5) không
        // chắc chắn được ASP.NET Core model binder parse đúng thành int[]/long[], có thể trả 400.
        if (Array.isArray(value)) {
          value.forEach((item) => paramKeys.push(key + '=' + item));
        } else {
          paramKeys.push(key + '=' + value);
        }
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  download = (url: string): Promise<AxiosResponse> => {
    return axios.get(url, { responseType: "blob" });
  };

  /**
   * Posts the given data to the URL
   */
  create = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.post(url, data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<AxiosResponse> => {
    return axios.put(url, data);
  };

  /**
   * Deletes data
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

/**
 * Lấy thông tin user đã đăng nhập từ localStorage
 * @returns User object hoặc null nếu chưa đăng nhập
 */
const getLoggedinUser = () => {
  const user = localStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };
